/**
 * Centralized API utility with automatic token refresh
 *
 * Uses the shared axios instance from utils/axios to prevent duplicate
 * refresh token logic. Having two independent axios instances with separate
 * isRefreshing states caused race conditions where both would attempt
 * concurrent token refreshes, triggering token family revocation.
 *
 * Usage:
 * import { apiRequest } from 'utils/api';
 * const data = await apiRequest('/users/me');
 */
import axios from 'axios';
import axiosServices from './axios';

// Get API URL from runtime config (Docker envsubst) or Vite env (dev)
import { API_URL as _API_URL } from 'runtime-config';
export const API_URL = _API_URL;

// Re-export the shared axios instance (single refresh logic)
export const api = axiosServices;

/**
 * Make an authenticated API request
 * @param endpoint - API endpoint (e.g., '/users', '/projects/123')
 * @param options - Request options (method, body, headers, etc.)
 * @returns Parsed JSON response or null for 204/404
 */
/**
 * Upload a file via multipart/form-data
 * @param endpoint - API endpoint
 * @param formData - FormData containing file and fields
 * @param onProgress - Optional upload progress callback (0-100)
 * @returns Parsed JSON response
 */
export const apiUpload = async (
  endpoint: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
) => {
  try {
    const response = await api.request({
      url: endpoint,
      method: 'POST',
      data: formData,
      onUploadProgress: onProgress
        ? (progressEvent: any) => {
            const total = progressEvent.total || 1;
            const percent = Math.round((progressEvent.loaded * 100) / total);
            onProgress(percent);
          }
        : undefined,
    });

    if (response.status === 204) return null;
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;
      let errorMessage = 'Upload failed';
      if (responseData?.message) {
        errorMessage = Array.isArray(responseData.message) ? responseData.message.join('; ') : responseData.message;
      } else if (typeof responseData?.error === 'string') {
        errorMessage = responseData.error;
      }
      // Sanitize 5xx errors — never expose backend internals to users
      const status = error.response?.status ?? 500;
      if (status >= 500) {
        errorMessage = 'Something went wrong. Please try again.';
      }

      const errorToThrow = responseData || { message: errorMessage };
      errorToThrow.message = errorMessage;
      throw errorToThrow;
    }
    throw error;
  }
};

export const apiRequest = async (endpoint: string, options?: {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
}) => {
  try {
    const response = await api.request({
      url: endpoint,
      method: options?.method || 'GET',
      data: options?.body ? JSON.parse(options.body) : undefined,
      headers: options?.headers,
    });

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // 404 is not an error for GET requests - it just means "not found"
      if (error.response?.status === 404 && (options?.method || 'GET') === 'GET') {
        return null;
      }

      // Extract error message - handle multiple response formats
      const responseData = error.response?.data;
      let errorMessage = 'Request failed';

      // Format 1: { message: string | string[] }
      if (responseData?.message) {
        errorMessage = Array.isArray(responseData.message)
          ? responseData.message.join('; ')
          : responseData.message;
      }
      // Format 2: { error: { details: string[] } }
      else if (responseData?.error?.details && Array.isArray(responseData.error.details)) {
        errorMessage = responseData.error.details.join('; ');
      }
      // Format 3: { error: { message: string } }
      else if (responseData?.error?.message) {
        errorMessage = responseData.error.message;
      }
      // Format 4: { error: string }
      else if (typeof responseData?.error === 'string') {
        errorMessage = responseData.error;
      }

      // Sanitize 5xx errors — never expose backend internals to users
      const status = error.response?.status ?? 500;
      if (status >= 500) {
        errorMessage = 'Something went wrong. Please try again.';
      }

      // Throw the response data directly so callers can inspect error details
      // Add message property for backwards compatibility with code expecting Error objects
      const errorToThrow = responseData || { message: errorMessage };
      errorToThrow.message = errorMessage;
      throw errorToThrow;
    }
    throw error;
  }
};
